import { FC, useEffect, useRef, useState } from "react";
import { KTSVG } from "../../_metronic/helpers";
import { currencyFormatter, customNumberFormat, customThousandSeparatorDot, getDateSummary } from "../../functions/general";
import { Pipeline } from "../../interfaces/Pipeline";
import { useIntl } from "react-intl";
import moment from "moment";
import { CustomField } from "../../interfaces/Settings";
import { useSettingsStore } from "../../stores/SettingsStore";
import useUser from "../../hooks/User";
import { useCustomeStore } from "../../stores/CustomeStore";
import { ExpandableElement } from "../General/ExpandableElement";
import { usePipelineStore } from "../../stores/PipelineStore";
import { returnCustomFieldMeeting, returnCustomFieldMerchant, returnCustomFieldPipeline, returnFile, returnImage, returnInvoiceReminder, returnLabel, returnLink, returnMerchantNamePipeline, returnMultiple, returnOption, returnParagraph, returnProduct, returnProgress, returnQtyProduct, returnSource } from "../General/DetailMasterElement";
import useModalEffect from "../../hooks/useModalEffect";
import { DetailProductPipeline } from "./DetailProductPipeline";

interface DetailPipelineProps {
    data: Pipeline
}

const DetailPipeline: FC<DetailPipelineProps> = ({ data }) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const intl = useIntl()
    const [customLead, setCustomLead] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const { settings } = useSettingsStore()
    const user = useUser()
    const { companyId, keyCustomfield } = useCustomeStore()
    const { simplePipelines, selectMultiPipeline } = usePipelineStore()
    const [showDetailProductModal, setShowDetailProductModal] = useState<boolean>(false);

    useEffect(() => {
        const customData: any = {
            date_created: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
                value: moment(data.date_created).format("DD MMM YYYY HH:mm")
            },
            merchant: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
                value: returnMerchantNamePipeline(data)
            },
            pic: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                value: data.owner_name
            },
            total_log: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
                value: data.lead_logs_count && customNumberFormat(data.lead_logs_count)
            },
            progress: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
                value: returnProgress(data, intl, companyId, user)
            },
            phone: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
                value: data.merchant_phone
            },
            age: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
                value: getDateSummary(data.lead_age)
            },
            source: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
                value: returnSource(data)
            },
            product: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
                value: returnProduct(data, setShowDetailProductModal)
            },
            ...(selectMultiPipeline.isFeatureLabelEnabled && {
                label: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
                    value: returnLabel(data)
                },
            }),
            ...(user.data.company_name === "Maspion Square"
                ? {
                    nominal_without_vat: {
                        name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL_BEFORE_VAT' }) + ` (${selectMultiPipeline.currencyCode})`,
                        value: `${data.nominal_without_vat && currencyFormatter(data.nominal_without_vat, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}`,
                    },
                }
                : {}),
            nominal: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + ` (${selectMultiPipeline.currencyCode})`,
                value: `${data.nominal && currencyFormatter(data.nominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}`,
            },
            product_qty: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUM_PRODUCTS' }),
                value: returnQtyProduct(data)
            },
            creator: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                value: data.creator_name
            },
            start_date: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
                value: moment(data.date_start).format("DD MMM YYYY HH:mm")
            },
            end_date: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
                value: data.date_end ? moment(data.date_end).format("DD MMM YYYY HH:mm") : "-"
            },
            last_update_date: {
                name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_LAST_UPDATED' }),
                value: data.date_last_update ? moment(data.date_last_update).format("DD MMM YYYY HH:mm") : "-"
            },
            note: {
                name: (user.data.company_id === companyId.elgisa || user.data.company_id === companyId.looyalIT) ? "Opportunity" : intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
                value: data.note ? <ExpandableElement text={(data.note).replaceAll('\n', '<br/>')} maxHeight={100} /> : "-"
            },
        }

        var newData = {
            ...customData,
            ...Object.keys(customFields).reduce((acc, key) => {
                let value;
                const dataCustomFields = data.custom_fields[key]
                const dataInfoCustomFields = (data as any)?.info_custom_fields?.[key];

                if (customFields[key].type === 'invoice_reminder' && dataCustomFields) {
                    if (JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                        value = returnInvoiceReminder(JSON.parse(dataCustomFields as string).invoice_url, JSON.parse(dataCustomFields as string).date_due, intl)
                    } else if (JSON.parse(dataCustomFields as string).invoice_url && !JSON.parse(dataCustomFields as string).date_due) {
                        value = <a href={JSON.parse(dataCustomFields as string).invoice_url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
                    } else if (!JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                        value = moment(JSON.parse(dataCustomFields as string).date_due).format("DD MMM YYYY")
                    } else value = "-"
                }
                else if (customFields[key].type === 'date') {
                    value = dataCustomFields ? moment(dataCustomFields).format("DD MMM YYYY") : "-";
                } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                    value = dataCustomFields ? returnImage(dataCustomFields, intl) : "-";
                } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                    value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                } else if (customFields[key].type === 'video' || customFields[key].type === 'videos') {
                    value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                    value = dataCustomFields ? returnMultiple(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'link') {
                    value = dataCustomFields ? returnLink(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'serial') {
                    value = typeof dataCustomFields === 'string' ? dataCustomFields : (dataCustomFields as string[])?.length > 0 ? (dataCustomFields as string[]).join('') : '-';
                } else if (customFields[key].type === 'merchant') {
                    value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                        <>
                            {index > 0 && ', '}
                            {returnCustomFieldMerchant(item)}
                        </>
                    )) : "-";
                } else if (customFields[key].type === 'user') {
                    value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.name).join(', ') : "-";
                } else if (customFields[key].type === 'dateTimeRange') {
                    value = dataCustomFields ? (dataCustomFields as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                } else if (customFields[key].type === 'pipeline') {
                    let result: any = simplePipelines.find((item: any) => item.id === (dataInfoCustomFields?.[0] as any)?.id)

                    if (result) {
                        let name = result.name + " / " + result.nominal + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                        let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                        let idPipeline = result.id
                        value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                    } else value = "-"
                } else if (customFields[key].type === 'meeting') {
                    value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                        <>
                            {index > 0 && ', '}
                            {returnCustomFieldMeeting(item)}
                        </>
                    )) : "-";
                } else if (customFields[key].type === 'time') {
                    value = dataCustomFields ? moment(dataCustomFields).format('HH:mm') : '-'
                } else if (customFields[key].type === 'dateTime') {
                    value = dataCustomFields ? moment(dataCustomFields).format('DD MMM YYYY HH:mm') : '-'
                } else if (customFields[key].type === 'number') {
                    value = dataCustomFields ? customThousandSeparatorDot(dataCustomFields as unknown as number) || "" : "-";
                } else if (customFields[key].type === 'option') {
                    value = dataCustomFields ? returnOption(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'paragraph') {
                    value = dataCustomFields ? returnParagraph(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'product') {
                    value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.product_name).join(', ') : "-";
                } else {
                    value = dataCustomFields ? dataCustomFields : "-";
                }

                return {
                    ...acc,
                    [key]: {
                        name: customFields[key].name,
                        value
                    }
                };
            }, {})
        };

        if (user.data.company_id === companyId.enterpriseSales) {
            newData = {
                ...newData,
                [keyCustomfield.serial.enterpriseSales]: {
                    name: 'Invoice',
                    value: data.custom_fields[keyCustomfield.serial.enterpriseSales] ? (data.custom_fields[keyCustomfield.serial.enterpriseSales] as string[]).join('') : "-"
                }
            }
        }
        if (user.data.company_id === companyId.enterpriseService) {
            newData = {
                ...newData,
                [keyCustomfield.serial.enterpriseService]: {
                    name: 'Invoice',
                    value: data.custom_fields[keyCustomfield.serial.enterpriseService] ? (data.custom_fields[keyCustomfield.serial.enterpriseService] as string[]).join('') : "-"
                }
            }
        }

        setCustomLead(newData);
    }, [customFields])

    useEffect(() => {
        if (Object.keys(selectMultiPipeline).length !== 0) {
            setCustomFields(selectMultiPipeline.customFields)
        }
    }, [selectMultiPipeline])

    useModalEffect({
        showModal: showDetailProductModal,
        setShowModal: setShowDetailProductModal,
        modalId: `detail-product-pipeline-modal-${data?.id}`
    })

    return (
        <div>
            <div className={`modal fade ${showDetailProductModal ? "hide" : "show"}`} tabIndex={-1} id={`detail-pipeline-modal`} ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Detail {data.merchant_name}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    {
                                        Object.keys(customLead).map((key, index) => {
                                            return (
                                                <div key={key + index} className="row pb-2 col-12 col-lg-6 col-xl-6">
                                                    <div className="col-3 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customLead[key].name}</div>
                                                    <div className="col-9" style={{ fontWeight: "500", color: "black", width: key === 'label' ? 'fit-content' : '' }}>{customLead[key].value}</div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CLOSE" })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {
                showDetailProductModal && <DetailProductPipeline pipeline={data! as Pipeline} />
            }
        </div>
    )
}

export { DetailPipeline }